// ---------------------------------------------------------------------------
// i18n – German translations (source of truth)
// ---------------------------------------------------------------------------

import type { Translations } from './types';

export const de: Translations = {
  common: {
    stationUnknown: (nr) => `Station ${nr} ist noch nicht freigeschaltet.`,
    stationOffline: 'Keine Verbindung — bitte später erneut scannen.',
    toMap: 'Zur Karte',
    more: 'Mehr',
    images: 'Bilder',
    retry: 'Erneut versuchen',
    back: 'Zurück',
    close: 'Schließen',
    processing: 'Anmeldung wird verarbeitet…',
  },

  landing: {
    hero: {
      title: 'Natur erleben in\nAugmented Reality',
      subtitle: () => `Bald verfügbar — registriere dich jetzt und erfahre als Erstes, wenn TSCHAPP live geht.`,
      cta: 'Jetzt registrieren',
      secondaryCta: 'Erkunden',
    },
    intro: {
      heading: 'Die Schlucht\nwird lebendig',
      text: (count) =>
        `TSCHAPP erweckt die Tscheppaschlucht in Ferlach zum Leben. Mit Augmented Reality kannst du über ${count} heimische Tier- und Pflanzenarten direkt in ihrer natürlichen Umgebung erkunden — mit detaillierten Beschreibungen, AI-generierten Wissensartikeln und immersiven 3D-Modellen.`,
    },
    stats: {
      animals: 'Tierarten',
      plants: 'Pflanzenarten',
      ar: 'Augmented Reality',
    },
    community: {
      heading: 'Werde Teil der Community',
      text: 'Mit einem Konto kannst du Arten liken, Kommentare hinterlassen und die Tscheppaschlucht mit anderen Naturbegeisterten erleben.',
      features: {
        like: {
          title: 'Arten liken',
          description: 'Markiere deine Lieblingsarten und erstelle deine persönliche Sammlung',
        },
        comment: {
          title: 'Kommentieren',
          description: 'Teile dein Wissen und tausche dich mit anderen Naturfreunden aus',
        },
        ar: {
          title: 'AR erleben',
          description: 'Erlebe die Arten in Augmented Reality direkt in der Schlucht',
        },
      },
      registerWithGoogle: 'Mit Google registrieren',
    },
    routes: {
      heading: 'Drei Wege, ein Erlebnis',
      text: 'Alle Routen starten am Besucherzentrum und führen durch die Tscheppaschlucht. An der Weggabelung wählst du dein Abenteuer.',
      stations: 'Stationen',
      detailLink: 'Alle Routen im Detail',
    },
    stories: {
      heading: 'Geschichten aus der Schlucht',
      text: 'Sagen, Legenden und die Geschichten unserer Maskottchen — erzählt als interaktives Hörbuch mit AI-Illustrationen.',
      narratorTschauko: 'Erzählt von Tschauko',
      narratorTschauka: 'Erzählt von Tschauka',
      scenes: 'Szenen',
      allLink: 'Alle Geschichten',
    },
    featured: {
      heading: 'Ausgewählte Arten',
      discoverLink: (count) => `Alle ${count} Arten entdecken`,
    },
  },

  browse: {
    badge: 'Flora & Fauna',
    heroTitle: 'Flora & Fauna',
    heroSubtitle: 'Entdecken Sie die Tier- und Pflanzenwelt der Tscheppaschlucht',
    speciesCount: (count) => `${count} Arten`,
    filterAll: 'Alle',
    logout: 'Abmelden',
    login: 'Anmelden',
    authBanner: 'Melde dich an, um Arten zu liken und Kommentare zu hinterlassen',
    close: 'Schließen',
  },

  species: {
    loading: 'Lade Objekt...',
    analyzing: 'Analysiere Objekt...',
    loading3d: 'Lade 3D Modell...',
    preparing3d: '3D-Modell wird vorbereitet …',
    load3dFailed: '3D-Modell konnte nicht geladen werden.',
    arStartFailed: 'AR konnte nicht gestartet werden. Bitte prüfe die WebXR-Berechtigung des Browsers.',
    notFound: 'Objekt nicht gefunden',
    analysisFailed: 'Analyse fehlgeschlagen',
    noId: 'Keine ID angegeben',
    brandSubtitle: 'Natur erleben in Augmented Reality',
    featuresCount: (count) => `${count} Merkmale`,
    share: 'Teilen',
    like: 'Like',
    unlike: 'Unlike',
    comments: 'Kommentare',
    audioGuide: {
      label: 'Audio Guide',
      subtitle: 'Beschreibung anhören',
      noAudio: 'Kein Audio',
    },
    pause: 'Pausieren',
    play: 'Abspielen',
    stopMusic: 'Musik stoppen',
    view2d: '2D Ansicht',
    view3d: '3D Ansicht',
    commentsSection: {
      title: (count) => `Kommentare (${count})`,
      empty: 'Noch keine Kommentare. Sei der Erste!',
      placeholder: 'Kommentar schreiben...',
      delete: 'Löschen',
    },
    close: 'Schließen',
    back: 'Zurück',
  },

  routes: {
    headerTitle: 'Wanderwege',
    heroTitle: 'Drei Wege durch\ndie Schlucht',
    heroSubtitle:
      'Alle Routen starten am Besucherzentrum und führen durch die beeindruckende Tscheppaschlucht. An der Weggabelung entscheidest du, welches Abenteuer auf dich wartet.',
    stats: {
      kilometers: 'Kilometer',
      walkingTime: 'Gehzeit',
      stations: 'Stationen',
    },
    sections: {
      routeCourse: 'Routenverlauf',
      elevationProfile: 'Höhenprofil',
      stations: 'Stationen',
    },
    listToggle: 'Liste',
    loading3d: 'Lade 3D-Ansicht\u2026',
    elevationMin: 'min',
    elevationMax: 'max',
    back: 'Zurück',
    details: {
      listenDescription: 'Beschreibung anhören',
      stopListening: 'Stopp',
      listenRoute: 'Route anhören',
      guideLive: 'Dr. Tschauko live',
      nextStations: 'Nächste Stationen',
    },
  },

  stories: {
    languageFallback: 'Diese Sage gibt es bisher nur auf Deutsch.',
    badge: 'Sagen & Geschichten',
    pageTitle: 'Sagen & Geschichten',
    pageSubtitle:
      'Die Mythen und Legenden der Tscheppaschlucht — erzählt von Tschauko & Tschauka',
    narratorTschauko: '\uD83E\uDD89 Erzählt von Tschauko',
    narratorTschauka: '\uD83D\uDC38 Erzählt von Tschauka',
    chapterCounter: (current, total) => `Kapitel ${current} von ${total}`,
    chapterCount: (count) => `${count} Kapitel`,
    startAudiobook: 'Hörbuch starten',
    closeAudiobook: 'Hörbuch schließen',
    previousChapter: 'Vorheriges Kapitel',
    nextChapter: 'Nächstes Kapitel',
    done: 'Fertig',
    chapterN: (n) => `Kapitel ${n}`,
    toggleMode: 'Modus wechseln',
    photoBackground: 'Foto-Hintergrund',
    back: 'Zurück',
  },

  footer: {
    tagline:
      'Natur erleben in Augmented Reality.',
    legal: {
      title: 'Rechtliches',
      imprint: 'Impressum',
      privacy: 'Datenschutz',
      terms: 'Nutzungsbedingungen',
    },
    contact: 'Kontakt',
    more: {
      title: 'Mehr',
      floraFauna: 'Flora & Fauna',
    },
    sponsoredBy: 'Gefördert von',
    copyright: (year) => `\u00A9 ${year} TSCHAPP \u00B7 Alle Rechte vorbehalten`,
  },

  units: {
    minuteShort: 'Min.',
    hourShort: 'Std.',
  },

  mapMarkers: {
    ticketOffice: 'Kassa',
    receptionHouse: 'Empfangshaus',
    goal: 'Ziel',
  },

  legend: {
    title: 'Legende',
    mainRoute: 'Hauptroute',
    sideTrail: 'Nebenweg',
    waterways: 'Gewässer',
    filterOnly: (kategorie: string) => `Nur ${kategorie} anzeigen`,
    filterClear: 'Filter aufheben — alle anzeigen',
    byDistance: 'Entfernung',
    byName: 'Name',
  },

  nav: {
    home: 'Startseite',
    floraFauna: 'Flora & Fauna',
    routes: 'Wanderwege',
    stories: 'Geschichten',
    login: 'Anmelden',
    logout: 'Abmelden',
    myProfile: 'Mein Bereich',
  },

  dialog: {
    register: {
      title: 'Registrieren',
      description:
        'Werde Teil der Tscheppaschlucht Community. Entdecke die Natur in Augmented Reality.',
      nameLabel: 'Name',
      namePlaceholder: 'Dein Name',
      emailLabel: 'E-Mail',
      emailPlaceholder: 'name@beispiel.at',
      passwordLabel: 'Passwort',
      passwordPlaceholder: 'Mind. 8 Zeichen',
      submit: 'Konto erstellen',
      google: 'Mit Google registrieren',
      alreadyRegistered: 'Bereits registriert?',
      loginLink: 'Anmelden',
      or: 'oder',
      emailSent: 'Wir haben dir eine Bestätigung gesendet. Du kannst jetzt weitermachen!',
      emailHint: 'Wir informieren dich, sobald TSCHAPP live geht.',
    },
    login: {
      title: 'Anmelden',
      description:
        'Arten liken, Kommentare hinterlassen und die Tscheppaschlucht mit anderen Naturbegeisterten erleben.',
      emailLabel: 'E-Mail',
      emailPlaceholder: 'name@beispiel.at',
      passwordLabel: 'Passwort',
      passwordPlaceholder: '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022',
      submit: 'Anmelden',
      google: 'Mit Google anmelden',
      noAccount: 'Noch kein Konto?',
      registerLink: 'Registrieren',
    },
    survey: {
      title: 'Kurze Umfrage',
      description: 'Hilf uns, TSCHAPP noch besser zu machen — 4 kurze Fragen.',
      interestLabel: 'Was interessiert dich am meisten?',
      interestOptions: {
        ar: 'AR-Erlebnis in der Natur',
        flora: 'Flora & Fauna entdecken',
        routes: 'Wanderrouten',
        stories: 'Geschichten & Sagen',
      },
      visitLabel: 'Wie oft besuchst du die Tscheppaschlucht?',
      visitOptions: {
        never: 'Noch nie',
        once: '1–2 Mal',
        regular: 'Regelmäßig',
        local: 'Ich wohne in der Nähe',
      },
      deviceLabel: 'Welches Gerät nutzt du?',
      deviceOptions: {
        iphone: 'iPhone',
        android: 'Android',
      },
      wishLabel: 'Was würdest du dir von der App wünschen?',
      wishPlaceholder: 'Deine Ideen (optional)',
      submit: 'Absenden',
      skip: 'Überspringen',
    },
  },

  navigation: {
    button: 'Navigation',
    title: 'Karte',
    close: 'Karte schließen',
    centerOnUser: 'Eigenen Standort zentrieren',
    nearestPoint: 'Nächster Punkt',
    distance: (meters) => meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`,
    learnMore: 'Mehr erfahren',
    playAudio: 'Audio abspielen',
    locationPermissionDenied: 'Standortzugriff wurde verweigert',
    loadingMap: 'Karte wird geladen…',
    noGps: 'Kein GPS-Signal',

    loadFailed: 'Karte konnte nicht geladen werden',
    aria: {
      zoomIn: 'Hineinzoomen',
      zoomOut: 'Herauszoomen',
      recenter: 'Auf meinen Standort zentrieren',
      followEnable: 'Karte meinem Standort folgen lassen',
      followDisable: 'Standort-Nachführung ausschalten',
    },
    aux: {
      storyGuideOpen: 'Story Guide öffnen',
      storyGuideClose: 'Story Guide schließen',
      aiCompanionStart: 'AI Begleiter starten',
      aiCompanionPause: 'AI Begleiter pausieren',
      photoMap: 'Foto-Karte anzeigen',
      vectorMode: 'Vektor-Modus',
      diaryEnd: 'Tagebuch beenden',
    },
    optionsSheet: {
      adventure: 'Adventure',
      performance: 'Performance',
      map: 'Karte',
      narrator: 'Erzähler',
      voiceComing: 'Stimme folgt bald',
    },
    poiCard: {
      audioBtn: 'Audio',
      audioStop: 'Stop',
      moreBtn: 'Mehr →',
      endBtn: 'Beenden',
      metersAway: 'm entfernt',
      fromStart: 'ab Start',
      away: 'entfernt',
      sightLabel: 'Sehenswürdigkeit',
    },
    // Routen-Karte (Picker) — Auswaehlen/Starten/Details (Alex 25.07.).
    /** Weggabelung: Routenwechsel direkt im Detail des Punktes
     *  (Alex 2026-08-11 — „wenn man da ist, will man vielleicht doch eine
     *  andere Route wandern"). */
    junction: {
      title: 'Von hier aus',
      hint: 'Diese Wege zweigen ab. Tippen wechselt die Route.',
      active: 'Aktive Route',
    },
    routeCard: {
      select: 'Auswählen',
      active: 'Aktiv',
      start: 'Route starten',
      details: 'Details',
    },
  },

  // Entdecken-Oberflaeche. NUR Bedienelemente — POI-Titel, Beschreibungen
  // und Kategorien kommen sprachselektiert aus ArTrack/knowledge-api.
  discover: {
    title: 'Entdecken',
    close: 'Schließen',
    back: 'Zurück',
    searchPlaceholder: 'Suchen nach Orten, Geschichten, Tieren …',
    searchLabel: 'Entdecken durchsuchen',
    categoryFilterLabel: 'Nach Kategorie filtern',
    detailLabel: 'Ortsdetail',
    unavailable: 'Dieser Ort ist derzeit nicht verfügbar.',
    highlights: 'Highlights',
    nearby: 'In der Nähe',
    allPlaces: 'Alle Orte',
    categories: 'Kategorien',
    filterAll: 'Alle',
    unitSpecies: 'Arten',
    unitPlaces: 'Orte',
    heroSubtitle: 'Geschichten, Natur und Geheimnisse der Tscheppaschlucht.',
    openMap: 'Karte öffnen',
    showAll: 'Alle anzeigen',
    showOnMap: 'Auf der Karte',
    readAloud: 'Vorlesen',
    loading: 'Inhalte werden geladen …',
    nothingFound: (suche: string) => suche
      ? `Nichts gefunden für „${suche}“.`
      : `Nichts gefunden in dieser Kategorie.`,
    stopReading: 'Vorlesen stoppen',
  },

  footerNav: {
    profileSignIn: 'Profil — anmelden',
    profile: 'Profil',
    map: 'Karte',
    discover: 'Entdecken',
    chatBot: 'Dr. Tschauko',
    diary: 'Tagebuch',
    debug: 'Debug',
    route: 'Route',
  },

  onboarding: {
    welcome: {
      title: 'Danke für deine Registrierung!',
      text: 'TSCHAPP bringt dir die Natur der Tscheppaschlucht näher — mit Augmented Reality, interaktiven Karten und spannenden Geschichten. Hier ein kurzer Überblick.',
    },
    flora: {
      title: 'Flora & Fauna',
      text: 'Entdecke über 80 heimische Tier- und Pflanzenarten mit detaillierten Beschreibungen, 3D-Modellen und AI-generierten Wissensartikeln. Jede Art kannst du liken und kommentieren.',
    },
    stories: {
      title: 'Geschichten & Sagen',
      text: 'Erlebe die Mythen und Legenden der Tscheppaschlucht als interaktives Hörbuch — erzählt von unseren Maskottchen Tschauko & Tschauka, illustriert mit AI-generierten Bildern.',
    },
    routes: {
      title: 'Wanderrouten',
      text: 'Drei einzigartige Wege führen durch die Schlucht. Jede Route hat eigene Stationen mit AR-Erlebnissen, Höhenprofilen und interaktiven 3D-Ansichten.',
    },
    next: 'Weiter',
    prev: 'Zurück',
    skip: 'Überspringen',
    start: 'Los geht\u2019s',
  },

  expedition: {
    title: 'Meine Expedition',
    progress: (found, total) => `${found} von ${total} Geheimnissen entdeckt`,
    nextSecret: 'Nächstes Geheimnis finden',
    nextSecretSub: 'Folge dem nächsten Hinweis und entdecke die Schlucht.',
    today: 'Heute entdeckt',
    newFound: 'Neu entdeckt',
    nextHint: 'Nächster Hinweis',
    myFinds: 'Meine Funde',
    secretSlot: 'Geheimnis',
    emptyRoute: 'Für diesen Weg sind noch keine Geheimnisse erfasst.',
    badges: 'Abzeichen',
    badgeTracker: { title: 'Spurensucher', text: 'Du findest Hinweise, die andere übersehen.' },
    badgePlants: { title: 'Pflanzenkenner', text: 'Du erkennst die Pflanzen der Schlucht.' },
    missionTitle: 'Dein Auftrag',
    secretNofM: (n, m) => `Geheimnis ${n} von ${m}`,
    lookLike: 'So könnte sie aussehen',
    whatYouGet: 'Was du bekommst',
    rewardFind: { title: 'Neuer Fund', sub: 'Entdecke etwas Besonderes.' },
    rewardProgress: { title: '+1 Fortschritt', sub: 'Deine Expedition wächst weiter.' },
    rewardSecret: { title: '1 Geheimnis näher', sub: 'Ein Schritt näher an der Lösung.' },
    rewardFindDone: 'Dieser Fund wurde zu deiner Sammlung hinzugefügt.',
    rewardSecretDone: 'Geheimnis gelöst',
    lookout: 'Ich halte Ausschau',
    foundIt: 'Ich habe es gefunden',
    later: 'Später',
    fundTitle: 'Super entdeckt',
    solvedNofM: (n, m) => `Geheimnis ${n} von ${m} gelöst`,
    youFound: (name) => `Du hast es gefunden: ${name}.`,
    progressLabel: 'Expedition-Fortschritt',
    intoBook: 'Ins Entdeckerbuch',
    learnMore: 'Mehr erfahren',
  },

  forschung: {
    title: 'Forschungstagebuch',
    subtitle: 'Beobachtungen festhalten und verstehen.',
    newObservation: '+ Neue Beobachtung',
    statBeobachtungen: 'Beobachtungen',
    statArten: 'Arten',
    statEntwuerfe: 'Entwürfe',
    drafts: 'Entwürfe',
    myEntries: 'Meine Einträge',
    emptyEntries: 'Noch keine Beobachtungen. Was du unterwegs siehst, hörst oder findest, hat hier Platz.',
    untitled: 'Ohne Titel',
    status: { draft: 'Entwurf', private: 'Privat', submitted: 'Wird geprüft', published: 'Veröffentlicht', rejected: 'Nicht veröffentlicht', queued: 'Wartet auf Upload', queuedRetry: 'Upload wartet (erneut)' },
    newTitle: 'Neue Beobachtung',
    gpsPending: 'Ort wird ergänzt, sobald GPS verfügbar ist.',
    capFoto: 'Foto',
    capMediathek: 'Mediathek',
    capAudio: 'Audio',
    capAudioStop: (s) => `${s}s Stopp`,
    capVideo: 'Video',
    mediaHint: (sec) => `Fotos werden fürs Hochladen automatisch verkleinert (HD). Videos: höchstens ${sec} Sekunden.`,
    videoTooLong: (sec, max) => `Das Video dauert ${sec} Sekunden — im Forschungstagebuch sind höchstens ${max} Sekunden vorgesehen, damit der Upload auch bei schwachem Empfang durchgeht.`,
    videoTooBig: (mb, max) => `Das Video ist zu groß (${mb} MB). Bitte höchstens ${max} Sekunden in HD aufnehmen.`,
    titleLabel: 'Titel',
    noteLabel: 'Notiz',
    notePlaceholder: 'Was hast du beobachtet?',
    defaultTitleAt: (ort) => `Beobachtung am ${ort}`,
    defaultTitle: 'Beobachtung',
    artLabel: 'Art verknüpfen (optional)',
    artSearch: 'Artname suchen …',
    artUnlink: 'Verknüpfung lösen',
    save: 'Eintrag speichern',
    saveHint: 'Gespeichert wird privat — nur für dich sichtbar. Einreichen für die Community ist danach ein eigener Schritt.',
    back: 'Zurück',
    suggest: 'Zur Veröffentlichung vorschlagen',
    previewHeading: 'So erscheint dein Beitrag',
    previewHint: 'Dein Eintrag wird geprüft, bevor er in der Community erscheint.',
    submit: 'Eintrag einreichen',
    submitting: 'Wird eingereicht …',
    keepPrivate: 'Privat behalten',
    edit: 'Bearbeiten',
    delete: 'Eintrag löschen',
    deleteConfirm: 'Eintrag endgültig löschen?',
    keep: 'Behalten',
    rejectedInfo: (reason) => `Die Redaktion hat diesen Beitrag nicht veröffentlicht${reason ? `: ${reason}` : '.'} Dein privater Eintrag bleibt bestehen.`,
    submitFailed: (detail) => `Einreichen fehlgeschlagen — der Eintrag bleibt privat gespeichert. (${detail})`,
    queuedInfo: (attempts) => `Eingereicht — der Upload läuft, sobald Verbindung da ist${attempts ? ` (bisher ${attempts} ${attempts === 1 ? 'Versuch' : 'Versuche'})` : ''}. Du kannst die App schließen; beim nächsten Öffnen wird weiter versucht.`,
    backToList: 'Zurück zur Übersicht',
    fallbackOrt: 'Tscheppaschlucht',
    community: 'Aus der Schlucht',
    communitySub: 'Die Community entdeckt, teilt und bewahrt.',
  },
};
